// ============================================================================
// Time & Labor — the punch surface (package-one test build).
//
// Two modes on one screen:
//   Staff test — pick a worker, pick the place, punch. This is the notional
//     test loop for Ben and Antonela.
//   Kiosk — worker + 4-digit PIN, facility fixed by the device registration.
//     This is the shape a wall-mounted iPad will run; in package one it still
//     rides a signed-in staff session (the unauthenticated worker door — SMS /
//     device token — is a later package with its own audited entry).
//
// ⛔ D-03 GOVERNS THIS FILE: there is no error state for any location outcome.
//    Permission refused, no GPS, weak GPS, outside the fence — every one is a
//    SUCCESSFUL punch with a different calm chip. The only refusals are
//    identity (wrong PIN, separated worker) — and those name themselves.
// ============================================================================

import { useEffect, useMemo, useRef, useState } from 'react';
import { Button, PageHeader, TabBar } from '../../shared/components';
import { errorMessage } from '../../shared/errors';
import { GeofenceChip } from './console-parts';
import { formatSiteDateTime, isoToSiteInput, siteInputToISO, tzLongLabel } from '../../lib/siteTime';
import { useTimeTabsNav } from './time-console';
import { WorkerPicker } from './worker-picker';
import {
  deviceFingerprint,
  punch,
  registerDevice,
  resolveAutoclose,
  useFacilityOptions,
  useServiceLines,
  useWorkerPicker,
  type AutocloseResolution,
} from './hooks/use-time-data';
import type { LocationState, PendingAutoclose, PunchResult } from './types';

interface Coords {
  latitude: number | null;
  longitude: number | null;
  accuracy: number | null;
  state: LocationState;
}

function captureLocation(): Promise<Coords> {
  return new Promise((resolve) => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      resolve({ latitude: null, longitude: null, accuracy: null, state: 'unavailable' });
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({
        latitude: pos.coords.latitude,
        longitude: pos.coords.longitude,
        accuracy: pos.coords.accuracy,
        state: null,
      }),
      (err) => resolve({
        latitude: null, longitude: null, accuracy: null,
        state: err.code === err.PERMISSION_DENIED ? 'declined' : 'unavailable',
      }),
      { enableHighAccuracy: true, timeout: 8000, maximumAge: 30000 },
    );
  });
}

// ⛔ The prompt's times and its "when did you leave" field are on the WORKER's
// clock — the building's market — never the browser's (Ben, 2026-09-24).

// ---------------------------------------------------------------------------
// The next-punch prompt (Decision 1, 2026-08-28). An auto-closed shift rides
// the NEXT punch as this card:
//   NON-BLOCKING — the punch it rides on was already recorded; nothing here
//     gates punching (D-03's cousin: the timeclock always works).
//   NON-DISMISSIBLE — there is no ✕. The card leaves when answered, or steps
//     aside for ~10h on "answer later", and returns on the punch after that.
// Asymmetric verification: "forgot to punch out" (LESS pay) is auto-accepted;
// "worked the full time" (MORE pay) routes to HR to confirm before payroll.
// The lazy tap is not the expensive one.
// ---------------------------------------------------------------------------
function AutoclosePromptCard({ items, onSettled }: {
  items: PendingAutoclose[];
  onSettled: (exceptionId: string, message: string) => void;
}) {
  const [activeId, setActiveId] = useState<string | null>(null);
  const [mode, setMode] = useState<Exclude<AutocloseResolution, 'defer'> | null>(null);
  const [outAt, setOutAt] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  function openAction(item: PendingAutoclose, m: Exclude<AutocloseResolution, 'defer'>) {
    setActiveId(item.exception_id);
    setMode(m);
    setOutAt(isoToSiteInput(item.auto_closed_at, item.site_tz));
    setNote('');
    setErr(null);
  }

  async function submit(item: PendingAutoclose) {
    if (!mode) return;
    setBusy(true);
    setErr(null);
    try {
      if (mode === 'forgot_out') {
        const r = await resolveAutoclose(item.exception_id, 'forgot_out', siteInputToISO(outAt, item.site_tz));
        onSettled(item.exception_id,
          `Thanks — shift corrected to ${((r.raw_minutes ?? 0) / 60).toFixed(2)} h. Done.`);
      } else if (mode === 'worked_full') {
        await resolveAutoclose(item.exception_id, 'worked_full', null, note.trim() || null);
        onSettled(item.exception_id,
          'Thanks — sent to HR to confirm the full hours before payroll.');
      } else {
        await resolveAutoclose(item.exception_id, 'not_sure', null, note.trim());
        onSettled(item.exception_id, 'Thanks — HR will look at this shift and follow up.');
      }
      setActiveId(null);
      setMode(null);
    } catch (e) {
      setErr(errorMessage(e, 'Could not save your answer.'));
    } finally {
      setBusy(false);
    }
  }

  async function deferItem(item: PendingAutoclose) {
    setBusy(true);
    setErr(null);
    try {
      await resolveAutoclose(item.exception_id, 'defer');
      onSettled(item.exception_id, 'Okay — this will come back on a later punch.');
    } catch (e) {
      setErr(errorMessage(e, 'Could not put this off.'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="rounded-xl border border-st-warn-border bg-st-warn-bg px-4 py-3">
      <div className="text-sm font-bold text-st-warn-fg">
        Before you go — {items.length === 1 ? 'a shift needs' : `${items.length} shifts need`} your answer
      </div>
      {items.map((item) => {
        const isActive = activeId === item.exception_id;
        return (
          <div key={item.exception_id} className="mt-2 rounded-lg border border-tcs-border bg-tcs-raised p-3">
            <div className="text-sm text-tcs-text">
              You punched in <span className="font-semibold">{formatSiteDateTime(item.started_at, item.site_tz)}</span> at{' '}
              <span className="font-semibold">{item.place_display}</span> and never punched out.
              {item.scheduled_hours != null && (
                <> Your scheduled shift there is <span className="font-semibold">{item.scheduled_hours} h</span>.</>
              )}{' '}
              The system closed it automatically at <span className="font-semibold">{formatSiteDateTime(item.auto_closed_at, item.site_tz)}</span>
              {item.raw_minutes != null && <> ({(item.raw_minutes / 60).toFixed(1)} h on the record)</>}.
            </div>
            <div className="mt-2 flex flex-wrap gap-2">
              <button type="button" disabled={busy}
                onClick={() => openAction(item, 'forgot_out')}
                className={`rounded-lg px-3 py-1.5 text-xs font-semibold ${isActive && mode === 'forgot_out'
                  ? 'bg-tcs-inverse text-tcs-inverse-ink'
                  : 'border border-tcs-border-strong bg-tcs-bg text-tcs-text hover:bg-tcs-surface'}`}>
                I forgot to punch out
              </button>
              <button type="button" disabled={busy}
                onClick={() => openAction(item, 'worked_full')}
                className={`rounded-lg px-3 py-1.5 text-xs font-semibold ${isActive && mode === 'worked_full'
                  ? 'bg-tcs-inverse text-tcs-inverse-ink'
                  : 'border border-tcs-border-strong bg-tcs-bg text-tcs-text hover:bg-tcs-surface'}`}>
                I worked that whole time
              </button>
              <button type="button" disabled={busy}
                onClick={() => openAction(item, 'not_sure')}
                className={`rounded-lg px-3 py-1.5 text-xs font-semibold ${isActive && mode === 'not_sure'
                  ? 'bg-tcs-inverse text-tcs-inverse-ink'
                  : 'border border-tcs-border-strong bg-tcs-bg text-tcs-text hover:bg-tcs-surface'}`}>
                I'm not sure
              </button>
              <button type="button" disabled={busy}
                onClick={() => void deferItem(item)}
                className="rounded-lg px-3 py-1.5 text-xs font-medium text-tcs-text-secondary hover:bg-tcs-surface">
                Answer later
              </button>
            </div>
            {isActive && mode === 'forgot_out' && (
              <div className="mt-3 flex flex-wrap items-end gap-2">
                <div>
                  <label className="text-xs font-semibold text-tcs-text-secondary" htmlFor={`ac-out-${item.exception_id}`}>
                    When did you actually leave? ({tzLongLabel(item.site_tz)})
                  </label>
                  <input
                    id={`ac-out-${item.exception_id}`} type="datetime-local" value={outAt}
                    min={isoToSiteInput(item.started_at, item.site_tz)} max={isoToSiteInput(item.auto_closed_at, item.site_tz)}
                    onChange={(e) => setOutAt(e.target.value)}
                    className="mt-1 block rounded-lg border border-tcs-border-input bg-tcs-field px-3 py-2 text-sm text-tcs-text"
                  />
                  <div className="mt-1 text-[11px] text-tcs-text-muted">
                    Between your punch-in and the auto-close — it can only shorten the shift, so it saves right away.
                  </div>
                </div>
                <Button size="sm" loading={busy} onClick={() => void submit(item)}>Save punch-out</Button>
              </div>
            )}
            {isActive && (mode === 'worked_full' || mode === 'not_sure') && (
              <div className="mt-3 flex flex-col gap-2">
                <textarea
                  value={note} rows={2}
                  onChange={(e) => setNote(e.target.value)}
                  className="w-full rounded-lg border border-tcs-border-input bg-tcs-field px-3 py-2 text-sm text-tcs-text"
                  placeholder={mode === 'worked_full'
                    ? 'Anything HR should know? (optional)'
                    : 'Tell us what happened — a sentence is enough.'}
                  aria-label="Note for HR"
                />
                <div className="flex items-center gap-2">
                  <Button size="sm" loading={busy}
                    disabled={mode === 'not_sure' && note.trim() === ''}
                    onClick={() => void submit(item)}>
                    {mode === 'worked_full' ? 'Send to HR to confirm' : 'Send to HR'}
                  </Button>
                  <span className="text-[11px] text-tcs-text-muted">
                    {mode === 'worked_full'
                      ? 'Claiming the full hours needs HR confirmation before payroll.'
                      : 'HR will sort it out with you — nothing is lost.'}
                  </span>
                </div>
              </div>
            )}
            {isActive && err && <div className="mt-2 text-sm text-st-danger-fg">{err}</div>}
          </div>
        );
      })}
    </div>
  );
}

export function TimePunch() {
  const tabsNav = useTimeTabsNav('punch');
  const workers = useWorkerPicker();
  const facilities = useFacilityOptions();
  const serviceLines = useServiceLines();

  const [mode, setMode] = useState<'staff' | 'kiosk'>('staff');
  const [workerId, setWorkerId] = useState('');
  const [placeKind, setPlaceKind] = useState<'facility' | 'market'>('facility');
  const [facilityId, setFacilityId] = useState('');
  const [marketRegion, setMarketRegion] = useState('');
  const [serviceLine, setServiceLine] = useState('evs');
  const [shiftCode, setShiftCode] = useState('');
  const [pin, setPin] = useState('');
  const [busy, setBusy] = useState<'in' | 'out' | null>(null);
  const [result, setResult] = useState<PunchResult | null>(null);
  const [refusal, setRefusal] = useState<string | null>(null);
  const [deviceName, setDeviceName] = useState('');
  const [deviceSaved, setDeviceSaved] = useState<string | null>(null);
  const [pending, setPending] = useState<PendingAutoclose[]>([]);
  const [settledMsg, setSettledMsg] = useState<string | null>(null);
  const startedAtRef = useRef<number | null>(null);
  const [elapsed, setElapsed] = useState('');

  const worker = useMemo(() => workers.rows.find((w) => w.worker_id === workerId), [workers.rows, workerId]);

  // Elapsed ticker after a successful punch-in.
  useEffect(() => {
    if (!result || result.auto_voided || !result.segment_id || busy) return undefined;
    const id = setInterval(() => {
      if (startedAtRef.current == null) return;
      const s = Math.floor((Date.now() - startedAtRef.current) / 1000);
      const h = Math.floor(s / 3600);
      const m = Math.floor((s % 3600) / 60);
      setElapsed(`${h}:${String(m).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`);
    }, 1000);
    return () => clearInterval(id);
  }, [result, busy]);

  async function doPunch(eventType: 'in' | 'out') {
    setRefusal(null);
    setBusy(eventType);
    try {
      const coords = mode === 'kiosk'
        ? { latitude: null, longitude: null, accuracy: null, state: null as LocationState }
        : await captureLocation();
      const r = await punch({
        workerId,
        eventType,
        captureMode: mode === 'kiosk' ? 'kiosk_pin' : 'mobile_web',
        facilityId: placeKind === 'facility' ? facilityId : null,
        marketRegion: placeKind === 'market' ? marketRegion.trim() : null,
        serviceLineCode: serviceLine || null,
        shiftCode: shiftCode || null,
        latitude: coords.latitude,
        longitude: coords.longitude,
        gpsAccuracyM: coords.accuracy,
        locationState: coords.state,
        deviceFingerprint: deviceFingerprint(),
        deviceTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        kioskPin: mode === 'kiosk' ? pin : null,
      });
      if (r.refused) {
        // Record-then-refuse: a wrong PIN comes back as a payload (not a
        // thrown error) so the lockout counter commits server-side.
        setRefusal(r.message ?? 'That PIN is not right.');
        setPin('');
        return;
      }
      setResult(r);
      setPin('');
      startedAtRef.current = eventType === 'in' ? Date.now() : null;
      setElapsed('');
      // The next-punch prompt rides in on the punch payload. It replaces any
      // stale card (the RPC re-sends everything still open for this worker).
      setPending(r.pending_autocloses ?? []);
      setSettledMsg(null);
    } catch (e) {
      // Identity refusals only (wrong PIN, separated worker, missing scope) —
      // a location problem never lands here (D-03).
      setRefusal(errorMessage(e, 'Could not record the punch.'));
    } finally {
      setBusy(null);
    }
  }

  async function saveDeviceName() {
    try {
      await registerDevice(deviceName.trim(), mode === 'kiosk' ? 'kiosk' : 'desktop',
        mode === 'kiosk' && placeKind === 'facility' ? facilityId || null : null);
      setDeviceSaved(deviceName.trim());
    } catch (e) {
      setRefusal(errorMessage(e, 'Could not save the device name.'));
    }
  }

  const placeChosen = placeKind === 'facility' ? facilityId !== '' : marketRegion.trim() !== '';
  const ready = workerId !== '' && placeChosen && (mode !== 'kiosk' || pin.length >= 4);

  return (
    <div className="mx-auto max-w-3xl px-4 py-6">
      <PageHeader
        eyebrow="Time & Labor"
        title="Punch"
        meta="Test surface for the notional phase. No punch is ever refused for a location reason — every outcome below is a recorded punch."
      />
      <div className="mt-3"><TabBar {...tabsNav} /></div>

      <div className="mt-4 flex gap-2">
        {(['staff', 'kiosk'] as const).map((m) => (
          <button
            key={m}
            type="button"
            onClick={() => { setMode(m); setRefusal(null); }}
            className={`rounded-lg px-4 py-2 text-sm font-semibold ${
              mode === m
                ? 'bg-tcs-inverse text-tcs-inverse-ink'
                : 'border border-tcs-border-strong bg-tcs-bg text-tcs-text hover:bg-tcs-surface'
            }`}
          >
            {m === 'staff' ? 'Staff test punch' : 'Kiosk mode (PIN)'}
          </button>
        ))}
      </div>

      <div className="mt-4 flex flex-col gap-4 rounded-2xl border border-tcs-border bg-tcs-raised p-5">
        <div>
          <span className="text-xs font-semibold text-tcs-text-secondary">Worker</span>
          {/* ⛔ Was a flat 116-name <select> with no typing, in which Ben
              could not find William among five Rodriguezes. */}
          <div className="mt-1">
            <WorkerPicker
              rows={workers.rows}
              value={workerId || null}
              onChange={(id) => { setWorkerId(id); setResult(null); setPending([]); setSettledMsg(null); }}
              placeholder="Type a name…"
            />
          </div>
          {mode === 'kiosk' && worker && !worker.has_pin && (
            <div className="mt-1 text-xs text-st-warn-fg">
              No PIN set — add one on the Workers &amp; caps tab first.
            </div>
          )}
        </div>

        <div>
          <div className="flex items-center gap-3">
            <span className="text-xs font-semibold text-tcs-text-secondary">Where</span>
            <label className="flex items-center gap-1.5 text-sm text-tcs-text">
              <input type="radio" checked={placeKind === 'facility'} onChange={() => setPlaceKind('facility')} />
              Facility
            </label>
            <label className="flex items-center gap-1.5 text-sm text-tcs-text">
              <input type="radio" checked={placeKind === 'market'} onChange={() => setPlaceKind('market')} />
              Market (roving role)
            </label>
          </div>
          {placeKind === 'facility' ? (
            <select
              value={facilityId} onChange={(e) => setFacilityId(e.target.value)}
              className="mt-2 w-full rounded-lg border border-tcs-border-input bg-tcs-field px-3 py-2.5 text-sm text-tcs-text"
              aria-label="Facility"
            >
              <option value="">Choose a facility…</option>
              {facilities.rows.map((f) => (
                <option key={f.facility_id} value={f.facility_id}>{f.display}</option>
              ))}
            </select>
          ) : (
            <input
              value={marketRegion} onChange={(e) => setMarketRegion(e.target.value)}
              placeholder="e.g. Texas"
              className="mt-2 w-full rounded-lg border border-tcs-border-input bg-tcs-field px-3 py-2.5 text-sm text-tcs-text"
              aria-label="Market region"
            />
          )}
          <div className="mt-1 text-xs text-tcs-text-muted">
            A punch always carries a place (R-11) — a facility, or a market for roving roles like the
            Texas night managers. Unallocated hours are structurally impossible here.
          </div>
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div>
            <label className="text-xs font-semibold text-tcs-text-secondary" htmlFor="punch-line">Service line</label>
            <select
              id="punch-line" value={serviceLine} onChange={(e) => setServiceLine(e.target.value)}
              className="mt-1 w-full rounded-lg border border-tcs-border-input bg-tcs-field px-3 py-2.5 text-sm text-tcs-text"
            >
              {serviceLines.map((l) => (
                <option key={l.service_line_code} value={l.service_line_code}>{l.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold text-tcs-text-secondary" htmlFor="punch-shift">Shift</label>
            <select
              id="punch-shift" value={shiftCode} onChange={(e) => setShiftCode(e.target.value)}
              className="mt-1 w-full rounded-lg border border-tcs-border-input bg-tcs-field px-3 py-2.5 text-sm text-tcs-text"
            >
              <option value="">—</option>
              <option value="1st">1st Shift</option>
              <option value="2nd">2nd Shift</option>
              <option value="3rd">3rd Shift</option>
              <option value="night">Night</option>
              <option value="weekend">Weekend</option>
              <option value="flex">Flex</option>
            </select>
          </div>
        </div>

        {mode === 'kiosk' && (
          <div>
            <label className="text-xs font-semibold text-tcs-text-secondary" htmlFor="punch-pin">PIN (4–6 digits)</label>
            <input
              id="punch-pin" value={pin} inputMode="numeric" autoComplete="off" type="password"
              onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
              className="mt-1 w-40 rounded-lg border border-tcs-border-input bg-tcs-field px-3 py-2.5 text-center text-lg tracking-[0.4em] text-tcs-text"
            />
          </div>
        )}

        <div className="flex flex-wrap gap-3">
          <Button
            loading={busy === 'in'} disabled={!ready || busy !== null}
            onClick={() => void doPunch('in')}
            className="min-w-[160px]"
          >
            PUNCH IN
          </Button>
          <Button
            variant="secondary" loading={busy === 'out'} disabled={!ready || busy !== null}
            onClick={() => void doPunch('out')}
            className="min-w-[160px]"
          >
            PUNCH OUT
          </Button>
        </div>

        {refusal && (
          <div className="rounded-xl border border-st-danger-border bg-st-danger-bg px-4 py-3 text-sm text-st-danger-fg">
            {refusal}
          </div>
        )}

        {result && (
          <div className="rounded-xl border border-st-ok-border bg-st-ok-bg px-4 py-3">
            <div className="font-semibold text-st-ok-fg">
              {result.auto_voided
                ? 'Punch recorded — the in/out pair was seconds apart, so the shift auto-voided (both punches kept).'
                : result.suppressed_duplicate
                  ? 'Punch recorded — duplicate within the window, no second shift opened.'
                  : startedAtRef.current
                    ? `Punched in${elapsed ? ` · on the clock ${elapsed}` : ''}`
                    : `Punched out${result.minutes != null ? ` · ${(result.minutes / 60).toFixed(2)} h` : ''}`}
            </div>
            <div className="mt-1.5 flex flex-wrap items-center gap-2 text-xs text-tcs-text-secondary">
              <GeofenceChip result={result.geofence_result} />
              {result.trust_level && <span className="text-tcs-text-muted">trust: {result.trust_level}</span>}
              {result.segment_id && <span className="text-tcs-text-muted">{result.segment_id}</span>}
            </div>
          </div>
        )}

        {/* Next-punch prompt — the punch above is already recorded; this card
            never gates anything, and it has no ✕: it leaves when answered. */}
        {pending.length > 0 && (
          <AutoclosePromptCard
            items={pending}
            onSettled={(exceptionId, message) => {
              setPending((p) => p.filter((x) => x.exception_id !== exceptionId));
              setSettledMsg(message);
            }}
          />
        )}
        {settledMsg && (
          <div className="rounded-xl border border-st-ok-border bg-st-ok-bg px-4 py-3 text-sm text-st-ok-fg">
            {settledMsg}
          </div>
        )}
      </div>

      <div className="mt-4 rounded-2xl border border-tcs-border bg-tcs-raised p-5">
        <div className="text-sm font-semibold text-tcs-text">This device</div>
        <div className="mt-1 text-xs text-tcs-text-secondary">
          A browser cannot report its machine name, so give this one a name people recognize —
          it appears on every punch from here (“Front Office Desktop”, “Menorah EVS 2nd Floor Kiosk”).
          Naming it in kiosk mode with a facility chosen also binds it to that facility.
        </div>
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <input
            value={deviceName} onChange={(e) => setDeviceName(e.target.value)}
            placeholder={deviceSaved ?? 'Name this device…'}
            className="h-9 w-64 rounded-lg border border-tcs-border-input bg-tcs-field px-3 text-sm text-tcs-text"
            aria-label="Device name"
          />
          <Button size="sm" variant="secondary" disabled={deviceName.trim() === ''} onClick={() => void saveDeviceName()}>
            Save device name
          </Button>
          {deviceSaved && <span className="text-xs text-st-ok-fg">Saved as “{deviceSaved}”</span>}
        </div>
      </div>
    </div>
  );
}
